import {filter, last, isEmpty} from 'lodash-es';

import {Composition, CompositionInformation} from '@common/types/documents/composition.types';
import {DetailedViewData} from '@common/types/stores/detailedViewStore.types';
import {EnhancedResponse} from '@common/types/documents/apiResponses.types';
import documentTypes from '@common/constants/documents/documentTypes';

export const mapToCompositionInformation = (composition: Composition): CompositionInformation => {
  return {
    name: composition?.name,
    regulatoryZone: composition?.regulatoryZone?.name,
    type: composition?.compositionType?.name,
    reason: composition?.reason?.name,
    flavor: composition?.flavor?.name,
    formula: composition?.formula?.name,
    formulaCode: composition?.formula?.code,
    pid: composition?.pid?.name,
    factory: composition?.factory?.name,
    density: composition?.density ? `${composition.density?.value} ${composition.density?.unit}` : ''
  };
};

const mapToFeedingTable = (data: EnhancedResponse) => {
  const currentFileVersion = data.files?.currentVersion;
  const fileName = data.files?.versions[currentFileVersion]?.types['master']?.name;
  const information = last(filter(data.requests, ({revision}) => data.revisionCount === revision));
  const current = information?.current;
  const mixed = information?.mixed;

  let currentData: DetailedViewData[] = [{key: 'currentSection', label: '', rowTitle: 'Current'}];
  if (current) {
    currentData = currentData.concat([
      {key: 'currentCompositionName', label: 'Composition Name', value: current?.composition?.name},
      {key: 'currentProduct', label: 'Product', value: current?.product?.name},
      {key: 'currentDensityForFG', label: 'Density for FG', value: `${current.densityForFG?.value} ${current.densityForFG?.unit}`},
      {key: 'currentEnergyToCalculate', label: 'Energy to Calculate', value: current?.energyToCalculate},
      {key: 'currentEnergyOnPack', label: 'Energy on Pack', value: current?.onPackEnergies?.join(', ')},
      {key: 'currentTechnology', label: 'Technology', value: current?.technology?.name},
      {key: 'currentPackType', label: 'Pack Type', value: current?.packType},
      {key: 'currentWeights', label: 'Weights', value: current?.weights?.map(({value, unit}: {value: string; unit: string}) => `${value} ${unit}`).join(', ')},
      {key: 'currentPillar', label: 'Pillar', value: current?.pillars?.map(({name}: {name: string}) => name).join(', ')},
      {key: 'currentEnergyCategory', label: 'Energy Category', value: current?.energyCategory?.name},
      {key: 'currentFormulaCode', label: 'Formula Code', value: current?.formulaCode},
      {key: 'currentTexture', label: 'Texture', value: current?.texture},
      {key: 'currentRegulatoryZone', label: 'Regulatory Zone', value: current?.regulatoryZone?.name},
      {key: 'currentSpecies', label: 'Species', value: current?.species?.name}
    ]);
  } else {
    currentData.push({key: 'currentNoData', value: 'No data', label: ''});
  }

  let mixedData: DetailedViewData[] = [{key: 'mixedSection', label: '', rowTitle: 'Mixed'}];
  if (mixed) {
    mixedData = mixedData.concat([
      {key: 'mixedCompositionName', label: 'Composition Name', value: mixed?.composition?.name},
      {key: 'mixedProduct', label: 'Product', value: mixed?.product?.name},
      {key: 'mixedDensityForFG', label: 'Density for FG', value: `${mixed?.densityForFG?.value} ${mixed?.densityForFG?.unit}`},
      {key: 'mixedEnergyToCalculate', label: 'Energy to Calculate', value: mixed?.energyToCalculate},
      {key: 'mixedEnergyOnPack', label: 'Energy on Pack', value: mixed?.onPackEnergies?.join(', ')},
      {key: 'mixedTechnology', label: 'Technology', value: mixed?.technology?.name},
      {key: 'mixedPackType', label: 'Pack Type', value: mixed?.packType},
      {key: 'mixedWeights', label: 'Weights', value: mixed?.weights?.map(({value, unit}: {value: string; unit: string}) => `${value} ${unit}`).join(', ')},
      {key: 'mixedPillar', label: 'Pillar', value: mixed?.pillars?.map(({name}: {name: string}) => name).join(', ')},
      {key: 'mixedEnergyCategory', label: 'Energy Category', value: mixed?.energyCategory?.name},
      {key: 'mixedFormulaCode', label: 'Formula Code', value: mixed?.formulaCode},
      {key: 'mixedTexture', label: 'Texture', value: mixed?.texture},
      {key: 'mixedRegulatoryZone', label: 'Regulatory Zone', value: mixed?.regulatoryZone?.name},
      {key: 'mixedSpecies', label: 'Species', value: mixed?.species?.name}
    ]);
  } else {
    mixedData.push({key: 'mixedNoData', value: 'No data', label: ''});
  }

  return [
    // Information
    {key: 'informationSection', label: '', rowTitle: 'Information'},
    {key: 'idDate', label: 'Internal ID', value: data.idData},
    {key: 'status', label: 'Status', value: data.status},
    {key: 'usage', label: 'Usage', value: data.usageName},
    {key: 'filename', label: 'Filename', value: fileName},
    {key: 'uploadModeName', label: 'Upload File Mode', value: data.uploadModeName},

    // Current
    ...currentData,

    // Mixed
    ...mixedData
  ];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapCompositionDetailedViewData = (data: EnhancedResponse): {key: string; label: string; value?: any}[] => {
  let mappedData: DetailedViewData[] = [];
  switch (data.typeId) {
    case documentTypes.FEEDING_TABLE:
      mappedData = mapToFeedingTable(data);
      break;
    default:
      throw new Error(`Unsupported document type '${data.typeId}' for detailed view`);
  }

  return mappedData.filter(({value, rowTitle}) => !isEmpty(value) || !isEmpty(rowTitle));
};
